import React, { useRef, useEffect } from 'react';
import { Bell, FileCheck, Package, Settings } from 'lucide-react';

const NotificationPanel = ({
  notifications,
  isOpen,
  onClose,
  onMarkAsRead,
  onMarkAllAsRead,
  onNavigate
}) => {
  const panelRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const unreadCount = notifications.filter(n => !n.read).length;
  const typeIcons = {
    approval: FileCheck,
    order: Package,
    system: Settings
  };
  const typeColors = {
    approval: 'bg-orange-100 text-orange-600',
    order: 'bg-blue-100 text-blue-600',
    system: 'bg-purple-100 text-purple-600'
  };

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 animate-in fade-in slide-in-from-top-2 duration-200"
    >
      {/* 头部 */}
      <div className="flex items-center justify-between p-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Bell size={18} className="text-slate-600" />
          <span className="font-bold text-slate-800">通知中心</span>
          {unreadCount > 0 && (
            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={onMarkAllAsRead}
            className="text-xs text-blue-600 hover:underline"
          >
            全部已读
          </button>
        )}
      </div>

      {/* 通知列表 */}
      <div className="max-h-80 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            <Bell size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">暂无通知</p>
          </div>
        ) : (
          notifications.slice(0, 10).map(notification => {
            const Icon = typeIcons[notification.type] || Settings;
            return (
              <div
                key={notification.id}
                className={`flex gap-3 p-4 border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors ${
                  !notification.read ? 'bg-blue-50/50' : ''
                }`}
                onClick={() => {
                  onMarkAsRead(notification.id);
                  if (notification.link) {
                    onNavigate(notification.link);
                  }
                  onClose();
                }}
              >
                <div className={`p-2 rounded-lg shrink-0 ${typeColors[notification.type] || 'bg-slate-100 text-slate-600'}`}>
                  <Icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {notification.title}
                    </p>
                    {!notification.read && (
                      <span className="w-2 h-2 bg-red-500 rounded-full shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-slate-500 truncate mt-0.5">
                    {notification.content}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">{notification.time}</p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 底部 */}
      <div className="p-3 border-t border-slate-100 text-center">
        <button
          className="text-xs text-slate-500 hover:text-blue-600 transition-colors"
          onClick={onClose}
        >
          关闭
        </button>
      </div>
    </div>
  );
};

export default NotificationPanel;
